import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from "react";

import { authService } from "../api/service/auth.service";
import { User } from "../api/interface/user.interface";
import {
  getAccessToken,
  clearToken,
  setToken,
} from "../utils/token-cookie";

// ===============================
// TYPES
// ===============================

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// ===============================
// CONTEXT
// ===============================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ===============================
// PROVIDER
// ===============================

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isFetchingRef = useRef(false);

  // ===============================
  // LOAD USER (ANTI DUPLICATE CALL)
  // ===============================

  const loadUser = useCallback(async () => {
    const token = getAccessToken();

    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    if (isFetchingRef.current) return; // 🔥 bloque doublon

    isFetchingRef.current = true;

    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser ?? null);
    } catch {
      clearToken();
      setUser(null);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, []);

  // ===============================
  // LOGIN
  // ===============================

  const login = useCallback(
    async (token: string) => {
      setToken(token);
      await loadUser();
    },
    [loadUser]
  );

  // ===============================
  // LOGOUT
  // ===============================

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      clearToken();
      setUser(null);
    }
  }, []);

  // ===============================
  // INIT (SAFE FOR STRICT MODE)
  // ===============================

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!alive) return;
      await loadUser();
    })();

    return () => {
      alive = false;
    };
  }, [loadUser]);

  // ===============================
  // VALUE
  // ===============================

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
      refreshUser: loadUser,
    }),
    [user, isLoading, login, logout, loadUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ===============================
// HOOK
// ===============================

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth doit être utilisé dans AuthProvider");
  }

  return context;
};